<template>
  <div class="container grid-lg mb-2 p-0" aria-labelledby="header-title">
    <Header title="Options"></Header>

    <nav aria-label="Breadcrumb">
      <ul id="subheader" class="breadcrumb px-2">
        <li class="breadcrumb-item">
          <router-link to="/">Case</router-link>
        </li>
        <li class="breadcrumb-item" aria-current="page">Options</li>
      </ul>
    </nav>

    <div class="container grid-sm">
      <form class="form-horizontal">
        <div class="form-group">
          <div class="col-4 col-sm-12">
            <label class="form-label" for="base-mva">Base MVA</label>
          </div>
          <div class="col-8 col-sm-12">
            <div class="input-group">
              <input
                v-model.number="mpc.baseMVA"
                class="form-input"
                id="base-mva"
                inputmode="decimal"
              />
              <button
                v-on:click="mpc.baseMVA = 100"
                type="button"
                class="btn input-group-btn"
                title="Reset to 100"
                aria-label="Reset value to 100"
              >
                <rotate-ccw-icon size="1x" class="icon" />
              </button>
            </div>
            <p v-if="!$v.mpc.baseMVA" class="form-input-hint">
              Base MVA is required.
            </p>
            <p v-else-if="!$v.mpc.baseMVA.decimal" class="form-input-hint">
              Base MVA must be a decimal.
            </p>
            <p v-else-if="!$v.mpc.baseMVA.minValue" class="form-input-hint">
              Base MVA must be > 0.
            </p>
          </div>
        </div>

        <div class="form-group">
          <div class="col-4 col-sm-12">
            <label class="form-label">Power Flow Model</label>
          </div>
          <div class="col-8 col-sm-12">
            <div class="form-group">
              <label class="form-radio form-inline">
                <input
                  v-model="options.model"
                  type="radio"
                  name="model"
                  value="AC"
                />
                <i class="form-icon"></i> AC
              </label>
              <label class="form-radio form-inline">
                <input
                  v-model="options.model"
                  type="radio"
                  name="model"
                  value="DC"
                />
                <i class="form-icon"></i>
                DC
                <HelpButton section="dcModeling" kind="small" />
              </label>
            </div>
          </div>
        </div>

        <fieldset v-bind:disabled="dc">
          <h6>
            Power Flow
            <HelpButton section="pfoptionsTable" kind="small" />
          </h6>

          <div class="form-group">
            <div class="col-4 col-sm-12">
              <label class="form-label" for="pf-alg">Algorithm</label>
            </div>
            <div class="col-8 col-sm-12">
              <select v-model="options.pf.alg" class="form-select" id="pf-alg">
                <option value="NR">Newton's method</option>
                <option value="FDXB">Fast-Decoupled (XB version)</option>
                <option value="FDBX">Fast-Decoupled (BX version)</option>
                <option value="GS">Gauss-Seidel</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <div class="col-4 col-sm-12">
              <label class="form-label">
                Nodal Balance Equations
                <HelpButton section="balanceEquations" kind="small" />
              </label>
            </div>
            <div class="col-8 col-sm-12">
              <div class="form-group">
                <label class="form-radio form-inline">
                  <input
                    v-model.number="options.pf.current_balance"
                    v-bind:disabled="!nr"
                    type="radio"
                    name="pf-current-balance"
                    value="0"
                  />
                  <i class="form-icon"></i> Complex Power
                </label>
                <label class="form-radio form-inline">
                  <input
                    v-model.number="options.pf.current_balance"
                    v-bind:disabled="!nr"
                    type="radio"
                    name="pf-current-balance"
                    value="1"
                  />
                  <i class="form-icon"></i> Complex Current
                </label>
              </div>
            </div>
          </div>

          <div class="form-group">
            <div class="col-4 col-sm-12">
              <label class="form-label">
                Voltage Representation
                <HelpButton section="voltageRepresentation" kind="small" />
              </label>
            </div>
            <div class="col-8 col-sm-12">
              <div class="form-group">
                <label class="form-radio form-inline">
                  <input
                    v-model.number="options.pf.v_cartesian"
                    v-bind:disabled="!nr"
                    type="radio"
                    name="pf-v-cartesian"
                    value="0"
                  />
                  <i class="form-icon"></i> Polar Coordinates
                </label>
                <label class="form-radio form-inline">
                  <input
                    v-model.number="options.pf.v_cartesian"
                    v-bind:disabled="!nr"
                    type="radio"
                    name="pf-v-cartesian"
                    value="1"
                  />
                  <i class="form-icon"></i> Cartesian Coordinates
                </label>
              </div>
            </div>
          </div>

          <div
            v-if="nr"
            v-bind:class="{ 'has-error': $v.options.pf.nr.max_it.$invalid }"
            class="form-group"
          >
            <div class="col-4 col-sm-12">
              <label class="form-label" for="nr-max-it">
                Maximum Iterations
              </label>
            </div>
            <div class="col-8 col-sm-12">
              <div class="input-group">
                <input
                  v-model.number="options.pf.nr.max_it"
                  class="form-input"
                  id="nr-max-it"
                  inputmode="numeric"
                />
                <button
                  v-on:click="options.pf.nr.max_it = 10"
                  type="button"
                  class="btn input-group-btn"
                  title="Reset to 10"
                  aria-label="Reset value to 10"
                >
                  <rotate-ccw-icon size="1x" class="icon" />
                </button>
              </div>
              <p
                v-if="!$v.options.pf.nr.max_it.required"
                class="form-input-hint"
              >
                Maximum iterations is required.
              </p>
              <p
                v-else-if="!$v.options.pf.nr.max_it.integer"
                class="form-input-hint"
              >
                Maximum iterations must be an integer.
              </p>
              <p
                v-else-if="!$v.options.pf.nr.max_it.minValue"
                class="form-input-hint"
              >
                Maximum iterations must be > 1.
              </p>
            </div>
          </div>

          <div
            v-if="fd"
            v-bind:class="{ 'has-error': $v.options.pf.fd.max_it.$invalid }"
            class="form-group"
          >
            <div class="col-4 col-sm-12">
              <label class="form-label" for="fd-max-it">
                Maximum Iterations
              </label>
            </div>
            <div class="col-8 col-sm-12">
              <div class="input-group">
                <input
                  v-model.number="options.pf.fd.max_it"
                  class="form-input"
                  id="fd-max-it"
                  inputmode="numeric"
                />
                <button
                  v-on:click="options.pf.fd.max_it = 30"
                  type="button"
                  class="btn input-group-btn"
                  title="Reset to 30"
                  aria-label="Reset value to 30"
                >
                  <rotate-ccw-icon size="1x" class="icon" />
                </button>
              </div>
              <p
                v-if="!$v.options.pf.fd.max_it.required"
                class="form-input-hint"
              >
                Maximum iterations is required.
              </p>
              <p
                v-else-if="!$v.options.pf.fd.max_it.integer"
                class="form-input-hint"
              >
                Maximum iterations must be an integer.
              </p>
              <p
                v-else-if="!$v.options.pf.fd.max_it.minValue"
                class="form-input-hint"
              >
                Maximum iterations must be > 1.
              </p>
            </div>
          </div>

          <div
            v-bind:class="{ 'has-error': $v.options.pf.tol.$invalid }"
            class="form-group"
          >
            <div class="col-4 col-sm-12">
              <label class="form-label" for="pf-tol">
                Termination Tolerance
              </label>
            </div>
            <div class="col-8 col-sm-12">
              <div class="input-group">
                <input
                  v-model.number="options.pf.tol"
                  class="form-input"
                  id="pf-tol"
                  inputmode="decimal"
                />
                <button
                  v-on:click="options.pf.tol = 1e-8"
                  type="button"
                  class="btn input-group-btn"
                  title="Reset to 1e-8"
                  aria-label="Reset value to 1e-8"
                >
                  <rotate-ccw-icon size="1x" class="icon" />
                </button>
              </div>
              <p v-if="!$v.options.pf.tol.required" class="form-input-hint">
                Termination tolerance is required.
              </p>
              <p
                v-else-if="!$v.options.pf.tol.minValue"
                class="form-input-hint"
              >
                Termination tolerance must be > 0.
              </p>
            </div>
          </div>

          <div
            v-bind:class="{
              'has-error': $v.options.pf.enforce_q_lims.$invalid
            }"
            class="form-group"
          >
            <div class="col-4 col-sm-12">
              <label class="form-label" for="enforce-q-lims">
                Generator Q Limits
              </label>
            </div>
            <div class="col-8 col-sm-12">
              <div class="input-group">
                <label class="form-checkbox">
                  <input
                    v-model.number="options.pf.enforce_q_lims"
                    v-bind:true-value="1"
                    v-bind:false-value="0"
                    type="checkbox"
                    id="enforce-q-lims"
                  />
                  <i class="form-icon"></i> Enforce
                </label>
              </div>
              <p
                v-if="!$v.options.pf.enforce_q_lims.between"
                class="form-input-hint"
              >
                Enforce Q limits must be 0 or 1.
              </p>
            </div>
          </div>
        </fieldset>

        <fieldset v-bind:disabled="dc">
          <h6>
            Optimal Power Flow
            <HelpButton section="opfoptionsTable" kind="small" />
          </h6>

          <div class="form-group">
            <div class="col-4 col-sm-12">
              <label class="form-label">
                Nodal Balance Equations
                <HelpButton section="opfBalanceEquations" kind="small" />
              </label>
            </div>
            <div class="col-8 col-sm-12">
              <div class="form-group">
                <label class="form-radio form-inline">
                  <input
                    v-model.number="options.opf.current_balance"
                    type="radio"
                    name="opf-current-balance"
                    value="0"
                  />
                  <i class="form-icon"></i> Complex Power
                </label>
                <label class="form-radio form-inline">
                  <input
                    v-model.number="options.opf.current_balance"
                    type="radio"
                    name="opf-current-balance"
                    value="1"
                  />
                  <i class="form-icon"></i> Complex Current
                </label>
              </div>
            </div>
          </div>

          <div class="form-group">
            <div class="col-4 col-sm-12">
              <label class="form-label">
                Voltage Representation
                <HelpButton section="opfVoltage" kind="small" />
              </label>
            </div>
            <div class="col-8 col-sm-12">
              <div class="form-group">
                <label class="form-radio form-inline">
                  <input
                    v-model.number="options.opf.v_cartesian"
                    type="radio"
                    name="opf-v-cartesian"
                    value="0"
                  />
                  <i class="form-icon"></i> Polar Coordinates
                </label>
                <label class="form-radio form-inline">
                  <input
                    v-model.number="options.opf.v_cartesian"
                    type="radio"
                    name="opf-v-cartesian"
                    value="1"
                  />
                  <i class="form-icon"></i> Cartesian Coordinates
                </label>
              </div>
            </div>
          </div>

          <div class="form-group">
            <div class="col-4 col-sm-12">
              <label class="form-label" for="flow-lim">Branch Flow Limit</label>
            </div>
            <div class="col-8 col-sm-12">
              <select
                v-model="options.opf.flow_lim"
                class="form-select"
                id="flow-lim"
              >
                <option value="S">Apparent Power</option>
                <option value="P">Active Power</option>
                <option value="2">Active Power (P^2)</option>
                <option value="I">Current Magnitude</option>
              </select>
            </div>
          </div>

          <div
            v-bind:class="{ 'has-error': $v.options.opf.violation.$invalid }"
            class="form-group"
          >
            <div class="col-4 col-sm-12">
              <label class="form-label" for="violation">
                Constraint Violation Tol.
              </label>
            </div>
            <div class="col-8 col-sm-12">
              <div class="input-group">
                <input
                  v-model.number="options.opf.violation"
                  class="form-input"
                  id="violation"
                  inputmode="decimal"
                />
                <button
                  v-on:click="options.opf.violation = 5e-6"
                  type="button"
                  class="btn input-group-btn"
                  title="Reset to 5e-6"
                  aria-label="Reset value to 5e-6"
                >
                  <rotate-ccw-icon size="1x" class="icon" />
                </button>
              </div>
              <p
                v-if="!$v.options.opf.violation.required"
                class="form-input-hint"
              >
                Constraint violation tolerance is required.
              </p>
              <p
                v-else-if="!$v.options.opf.violation.minValue"
                class="form-input-hint"
              >
                Constraint violation tolerance must be > 0.
              </p>
            </div>
          </div>

          <div
            v-bind:class="{
              'has-error': $v.options.opf.ignore_angle_lim.$invalid
            }"
            class="form-group"
          >
            <div class="col-4 col-sm-12">
              <label class="form-label" for="ignore-angle-lim">
                Branch Angle Limits
              </label>
            </div>
            <div class="col-8 col-sm-12">
              <div class="input-group">
                <label class="form-checkbox">
                  <input
                    v-model.number="options.opf.ignore_angle_lim"
                    v-bind:true-value="1"
                    v-bind:false-value="0"
                    type="checkbox"
                    id="ignore-angle-lim"
                  />
                  <i class="form-icon"></i> Ignore
                </label>
              </div>
              <p
                v-if="!$v.options.opf.ignore_angle_lim.between"
                class="form-input-hint"
              >
                Ignore angle limits must be 0 or 1.
              </p>
            </div>
          </div>

          <div class="form-group">
            <div class="col-4 col-sm-12">
              <label class="form-label" for="start">
                Starting Point Strategy
              </label>
            </div>
            <div class="col-8 col-sm-12">
              <select
                v-model.number="options.opf.start"
                class="form-select"
                id="start"
              >
                <option value="0">Ignore Current State</option>
                <option value="2">Use Current State</option>
                <option value="3">Power Flow Result</option>
              </select>
            </div>
          </div>
        </fieldset>

        <fieldset>
          <h6>Advanced</h6>

          <div class="form-group">
            <div class="col-4 col-sm-12">
              <label class="form-label">User's Manual</label>
            </div>
            <div class="col-8 col-sm-12">
              <div class="form-group">
                <label class="form-radio form-inline">
                  <input
                    v-model="config.manual"
                    type="radio"
                    name="manual"
                    value="html"
                  />
                  <i class="form-icon"></i> HTML
                </label>
                <label class="form-radio form-inline">
                  <input
                    v-model="config.manual"
                    type="radio"
                    name="manual"
                    value="pdf"
                  />
                  <i class="form-icon"></i> PDF
                </label>
                <label class="form-radio form-inline">
                  <input
                    v-model="config.manual"
                    type="radio"
                    name="manual"
                    value="hide"
                  />
                  <i class="form-icon"></i> Hide
                </label>
              </div>
            </div>
          </div>

          <div
            v-bind:class="{ 'has-error': $v.config.debounce.$invalid }"
            class="form-group"
          >
            <div class="col-4 col-sm-12">
              <label class="form-label" for="nr-max-it">
                Debounce
              </label>
            </div>
            <div class="col-8 col-sm-12">
              <div class="input-group">
                <input
                  v-model.number="config.debounce"
                  class="form-input"
                  id="config-debounce"
                  inputmode="numeric"
                />
                <button
                  v-on:click="config.debounce = 1000"
                  type="button"
                  class="btn input-group-btn"
                  title="Reset to 1000"
                  aria-label="Reset value to 1000"
                >
                  <rotate-ccw-icon size="1x" class="icon" />
                </button>
              </div>
              <p v-if="!$v.config.debounce.required" class="form-input-hint">
                Debounce is required.
              </p>
              <p
                v-else-if="!$v.config.debounce.integer"
                class="form-input-hint"
              >
                Debounce must be an integer.
              </p>
              <p
                v-else-if="!$v.config.debounce.minValue"
                class="form-input-hint"
              >
                Debounce must be >= 100.
              </p>
            </div>
          </div>
        </fieldset>
      </form>
    </div>
  </div>
</template>

<script>
import { RotateCcwIcon } from "vue-feather-icons";

import {
  required,
  integer,
  decimal,
  minValue,
  between
} from "vuelidate/lib/validators";

import Header from "../components/Header";
import HelpButton from "../components/HelpButton";
import MPCStore from "../stores/MPCStore";
import OptionStore from "../stores/OptionStore";
import ConfigStore from "../stores/ConfigStore";

export default {
  name: "Options",
  components: { Header, HelpButton, RotateCcwIcon },
  data() {
    return {
      options: OptionStore.methods.getOptions(),
      config: ConfigStore.methods.getConfig(),
      mpc: MPCStore.methods.getMPC()
    };
  },
  computed: {
    dc() {
      return this.options.model === "DC";
    },
    nr() {
      switch (this.options.pf.alg) {
        case "NR":
        case "NR-SC":
        case "NR-IP":
        case "NR-IC":
          return true;
        default:
          return false;
      }
    },
    fd() {
      switch (this.options.pf.alg) {
        case "FDXB":
        case "FDBX":
          return true;
        default:
          return false;
      }
    }
  },
  validations() {
    return {
      options: {
        pf: {
          tol: {
            required,
            minValue: minValue(1e-15)
          },
          nr: {
            max_it: {
              required,
              integer,
              minValue: minValue(1)
            }
          },
          fd: {
            max_it: {
              required,
              integer,
              minValue: minValue(1)
            }
          },
          enforce_q_lims: {
            between: between(0, 1)
          }
        },
        opf: {
          violation: {
            required,
            minValue: minValue(1e-15)
          },
          ignore_angle_lim: {
            between: between(0, 1)
          }
        }
      },
      mpc: {
        baseMVA: {
          required,
          decimal,
          minValue: minValue(0)
        }
      },
      config: {
        debounce: {
          required,
          integer,
          minValue: minValue(100)
        }
      }
    };
  }
};
</script>

<style lang="scss" scoped>
@import "../main.scss";

fieldset {
  margin-top: 1rem;
  border-top: 0.05rem solid $gray-color;
  padding-top: 0.5rem;
}
</style>
